import {
  ActionSheetButton,
  ActionSheetController,
  ModalController,
  NavController,
} from 'ionic-angular';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';

import { InspectionFormService } from './inspection-form.service';
import { LoadingAlertProvider } from './../../../../providers/loading-alert/loading-alert';
import { OpsOrderModel, OrderStatus } from '../../../../models/order.ops';
import { OpsProvider } from './../../../../providers/ops/ops';
import {
  OrderCompletePage,
  OrderCompletePageProps,
} from './../../../order-complete/order-complete';
import { TruckModel } from './../../../../models/truck';

@Component({
  selector: 'order-detail-tasks-list',
  templateUrl: 'order-detail-tasks-list.html',
})
export class OrderDetailTasksListComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() order: OpsOrderModel;
  @Input() truck: TruckModel;

  tasks: Task[] = [];
  taskListInitialized: boolean;

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    private modalCtlr: ModalController,
    private actionSheetCtrl: ActionSheetController,
    private navCtrl: NavController,
    private opsService: OpsProvider,
    private loadingAlert: LoadingAlertProvider,
    private inspectionForm: InspectionFormService
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.order) {
      this.prepareTaskList();
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  // Tasks

  private prepareTaskList() {
    this.tasks = [this.completeTask()];
  }

  private completeTask(): Task {
    return {
      title: 'Complete Order',
      clickHandler: () => this.didTapCompleteOrder(),
      completed: () => this.order.status === OrderStatus.Complete,
      disabled: () => this.order.status === OrderStatus.Complete,
    };
  }

  taskComplete(task: Task) {
    return task.completed && task.completed();
  }

  // Data

  completeOrder(order: OpsOrderModel) {
    this.loadingAlert.showLoader();
    this.opsService.completeOrder(order).subscribe(
      () => {
        this.loadingAlert.dismissLoader();
        this.loadingAlert.showToastConfirmation('Order completed');
        this.navCtrl.pop();
      },
      () => {
        this.loadingAlert.dismissLoader();
        this.loadingAlert.showToastAlert('Error completing order');
      }
    );
  }

  // Actions

  didTapInspectionForm(url?: string) {
    const inspectionUrl = url || this.order.inspectionFormUrl;
    if (!inspectionUrl) {
      return;
    }
    this.inspectionForm.present(inspectionUrl).then(
      () => {
        this.completeOrder(this.order);
      },
      error => {
        this.loadingAlert.presentAlert(
          'Error completing inspection form',
          error
        );
      }
    );
  }

  didTapCompleteOrder() {
    if (!this.presentInspectionFormSheet()) {
      this.promptToCompleteOrder(this.order);
    }
  }

  private promptToCompleteOrder(order: OpsOrderModel) {
    const props: OrderCompletePageProps = {
      truck: this.truck,
      order,
      isClockedIn: this.opsService.currentFillerShift != null,
      isFleet: false,
      service: this.order.service,
    };
    let modal = this.modalCtlr.create(OrderCompletePage, { props });
    modal.onDidDismiss(data => data && this.orderDidComplete(data));
    modal.present();
  }

  private presentInspectionFormSheet(): boolean {
    const buttons: ActionSheetButton[] = [];
    if (this.order.inspectionFormUrl) {
      buttons.push({
        text: 'Open Inspection Form',
        handler: () => this.didTapInspectionForm(this.order.inspectionFormUrl),
      });
    }
    if (this.order.inspectionFormAppUrl) {
      buttons.push({
        text: 'Open Inspection App',
        handler: () => this.didTapInspectionForm(this.order.inspectionFormAppUrl),
      });
    }
    if (!buttons.length) {
      return false;
    }
    buttons.push({ text: 'Cancel', role: 'cancel' });
    this.actionSheetCtrl
      .create({
        title: 'Complete inspection',
        buttons,
      })
      .present();
    return true;
  }

  showClockedOutWarning() {
    this.loadingAlert.presentAlert(
      'No Shift',
      'An order can only be completed during a shift. Please clock in and then come back to complete this order.'
    );
  }

  // Helpers

  disableItem() {
    return this.order.status === 'pending' && !this.order.virtual;
  }

  // Events

  orderDidComplete(data?) {
    this.order = data.order;
    this.opsService.getTruck(this.truck.uid).subscribe(truck => {
      this.truck = truck;
    });
  }
}

interface Task {
  title: string;
  clickHandler: () => void;
  disabled: () => boolean;
  completed?: () => boolean;
}



// WEBPACK FOOTER //
// ./src/pages/order-detail/components/order-detail-tasks-list/order-detail-tasks-list.ts